import React, { useState, useEffect } from 'react';
import { Trophy, Star, Award, Users, Crown, Zap, Heart, GraduationCap, CalendarCheck, Shield, Lightbulb, Plus, X } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { JewelModal, JewelInput, JewelTextarea, JewelSelect, JewelButton, JewelField } from '../../components/ui/jewel-modal';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BADGE_ICONS = {
  'star': Star, 'users': Users, 'lightbulb': Lightbulb, 'graduation-cap': GraduationCap,
  'calendar-check': CalendarCheck, 'heart': Heart, 'zap': Zap, 'trophy': Trophy
};

const GamificationPage = ({ getAuthHeaders, currentUser }) => {
  const [tab, setTab] = useState('leaderboard');
  const [leaderboard, setLeaderboard] = useState([]);
  const [points, setPoints] = useState({ points: 0, history: [] });
  const [badges, setBadges] = useState({ all_badges: [], earned: [] });
  const [sotm, setSotm] = useState({ current: {}, history: [] });
  const [nominations, setNominations] = useState({ nominations: [], tally: {}, month: '' });
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNominate, setShowNominate] = useState(false);
  const [showAwardBadge, setShowAwardBadge] = useState(false);
  const [showAwardPoints, setShowAwardPoints] = useState(false);
  const [nomForm, setNomForm] = useState({ nominee: '', reason: '', category: 'general' });
  const [badgeForm, setBadgeForm] = useState({ target_username: '', badge_id: '' });
  const [pointsForm, setPointsForm] = useState({ target_username: '', points: 10, reason: '' });
  const isMgmt = ['admin', 'executive', 'superadmin', 'manager'].includes(currentUser?.role);
  const isExec = ['executive', 'superadmin'].includes(currentUser?.role);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    const h = getAuthHeaders();
    try {
      const [lb, pt, bg, sm, nm, st] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/gamification/leaderboard`, { headers: h }),
        axios.get(`${BACKEND_URL}/api/gamification/points`, { headers: h }),
        axios.get(`${BACKEND_URL}/api/gamification/badges`, { headers: h }),
        axios.get(`${BACKEND_URL}/api/gamification/staff-of-month`, { headers: h }),
        axios.get(`${BACKEND_URL}/api/gamification/nominations`, { headers: h }),
        axios.get(`${BACKEND_URL}/api/admin/staff-accounts`, { headers: h }).catch(() => ({ data: [] }))
      ]);
      setLeaderboard(lb.data);
      setPoints(pt.data);
      setBadges(bg.data);
      setSotm(sm.data);
      setNominations(nm.data);
      setStaffList(st.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const nominate = async () => {
    if (!nomForm.nominee || !nomForm.reason) { toast.error('Fill all fields'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/gamification/nominations`, nomForm, { headers: getAuthHeaders() });
      toast.success('Nomination submitted!');
      setShowNominate(false);
      setNomForm({ nominee: '', reason: '', category: 'general' });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const awardBadge = async () => {
    if (!badgeForm.target_username || !badgeForm.badge_id) { toast.error('Select staff and badge'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/gamification/badges/award`, badgeForm, { headers: getAuthHeaders() });
      toast.success('Badge awarded!');
      setShowAwardBadge(false);
      setBadgeForm({ target_username: '', badge_id: '' });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const awardPoints = async () => {
    if (!pointsForm.target_username || !pointsForm.reason) { toast.error('Fill all fields'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/gamification/points/award`, pointsForm, { headers: getAuthHeaders() });
      toast.success('Points awarded!');
      setShowAwardPoints(false);
      setPointsForm({ target_username: '', points: 10, reason: '' });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const tabs = [
    { id: 'leaderboard', label: 'Leaderboard', icon: Trophy },
    { id: 'badges', label: 'Badges', icon: Award },
    { id: 'sotm', label: 'Staff of the Month', icon: Crown },
    { id: 'my-points', label: 'My Points', icon: Zap }
  ];

  const earnedBadgeIds = badges.earned.map(b => b.badge_id);

  return (
    <div className="space-y-6" data-testid="gamification-page">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-slate-900 via-amber-950/30 to-slate-900 border border-amber-500/20 p-6">
        <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl" />
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500/30 to-orange-600/30 flex items-center justify-center border border-amber-500/20">
              <Trophy className="w-7 h-7 text-amber-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Recognition & Rewards</h1>
              <p className="text-slate-400 text-sm">Leaderboard, badges, and nominations</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-2 text-center">
              <p className="text-amber-400 font-bold text-lg">{points.points}</p>
              <p className="text-slate-500 text-[10px]">Your Points</p>
            </div>
            {isMgmt && (
              <div className="flex gap-2">
                <Button onClick={() => setShowAwardBadge(true)} variant="outline" className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10" data-testid="award-badge-btn"><Award className="w-4 h-4 mr-1" />Badge</Button>
                <Button onClick={() => setShowAwardPoints(true)} variant="outline" className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10" data-testid="award-points-btn"><Zap className="w-4 h-4 mr-1" />Points</Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${tab === t.id ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'text-slate-400 hover:bg-slate-800/50 border border-transparent'}`}>
              <Icon className="w-4 h-4" />{t.label}
            </button>
          );
        })}
      </div>

      {loading ? <div className="text-center py-16 text-slate-500">Loading...</div> : (
        <>
          {/* Leaderboard Tab */}
          {tab === 'leaderboard' && (
            <div className="bg-slate-900/60 border border-slate-800/50 rounded-2xl overflow-hidden">
              <div className="p-4 border-b border-slate-800/50">
                <h2 className="text-white font-semibold">Points Leaderboard</h2>
              </div>
              {leaderboard.length === 0 ? (
                <div className="text-center py-12 text-slate-500">No points earned yet</div>
              ) : (
                <div className="divide-y divide-slate-800/50">
                  {leaderboard.map((user, i) => (
                    <div key={user.username} className={`flex items-center gap-4 p-4 ${i < 3 ? 'bg-gradient-to-r from-amber-500/5 to-transparent' : ''}`} data-testid={`leaderboard-row-${i}`}>
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm ${i === 0 ? 'bg-amber-500/20 text-amber-400' : i === 1 ? 'bg-slate-400/20 text-slate-300' : i === 2 ? 'bg-orange-500/20 text-orange-400' : 'bg-slate-800 text-slate-500'}`}>
                        {i < 3 ? <Crown className="w-5 h-5" /> : i + 1}
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-medium">{user.display_name}</p>
                        <p className="text-slate-500 text-xs">@{user.username} | {user.badge_count} badges</p>
                      </div>
                      <div className="text-right">
                        <p className="text-amber-400 font-bold">{user.points}</p>
                        <p className="text-slate-600 text-[10px]">points</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Badges Tab */}
          {tab === 'badges' && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {badges.all_badges.map(badge => {
                const earned = earnedBadgeIds.includes(badge.id);
                const Icon = BADGE_ICONS[badge.icon] || Star;
                return (
                  <div key={badge.id} className={`rounded-2xl p-5 text-center transition-all ${earned ? 'bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30' : 'bg-slate-900/40 border border-slate-800/50 opacity-60'}`} data-testid={`badge-${badge.id}`}>
                    <div className={`w-16 h-16 rounded-2xl mx-auto mb-3 flex items-center justify-center ${earned ? 'bg-amber-500/20' : 'bg-slate-800'}`}>
                      <Icon className={`w-8 h-8 ${earned ? 'text-amber-400' : 'text-slate-600'}`} />
                    </div>
                    <h3 className={`font-semibold ${earned ? 'text-white' : 'text-slate-500'}`}>{badge.name}</h3>
                    <p className="text-slate-500 text-xs mt-1">{badge.description}</p>
                    <Badge className={`mt-2 ${earned ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-700/50 text-slate-500'}`}>{badge.points} pts</Badge>
                  </div>
                );
              })}
            </div>
          )}

          {/* Staff of the Month Tab */}
          {tab === 'sotm' && (
            <div className="space-y-6">
              {sotm.current?.winner && (
                <div className="bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-red-500/10 border border-amber-500/30 rounded-2xl p-8 text-center">
                  <Crown className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                  <h2 className="text-2xl font-bold text-white">{sotm.current.winner}</h2>
                  <p className="text-amber-400 text-sm mt-1">Staff of the Month - {sotm.current.month}</p>
                  {sotm.current.reason && <p className="text-slate-400 mt-2 text-sm max-w-md mx-auto">{sotm.current.reason}</p>}
                </div>
              )}
              <div className="flex items-center justify-between">
                <h2 className="text-white font-semibold">Nominations ({nominations.month})</h2>
                <Button onClick={() => setShowNominate(true)} className="bg-gradient-to-r from-amber-500 to-orange-600 text-white" data-testid="nominate-btn"><Plus className="w-4 h-4 mr-2" />Nominate</Button>
              </div>
              {Object.keys(nominations.tally).length === 0 ? (
                <div className="text-center py-12 bg-slate-900/40 border border-slate-800/50 rounded-2xl"><Trophy className="w-12 h-12 text-slate-700 mx-auto mb-3" /><p className="text-slate-500">No nominations this month yet</p></div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                  {Object.entries(nominations.tally).sort((a, b) => b[1].count - a[1].count).map(([name, data]) => (
                    <div key={name} className="bg-slate-900/60 border border-slate-800/50 rounded-xl p-5 hover:border-amber-500/30 transition-all">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-white font-semibold">{name}</h3>
                        <Badge className="bg-amber-500/20 text-amber-400">{data.count} votes</Badge>
                      </div>
                      <div className="space-y-1">{data.reasons.slice(0, 3).map((r, i) => <p key={i} className="text-slate-500 text-xs truncate">"{r}"</p>)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* My Points Tab */}
          {tab === 'my-points' && (
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30 rounded-2xl p-6 text-center">
                <Zap className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
                <p className="text-3xl font-bold text-white">{points.points}</p>
                <p className="text-slate-400 text-sm">Total Points Earned</p>
              </div>
              <div className="bg-slate-900/60 border border-slate-800/50 rounded-2xl">
                <div className="p-4 border-b border-slate-800/50"><h2 className="text-white font-semibold">Points History</h2></div>
                {points.history.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">No points history yet</div>
                ) : (
                  <div className="divide-y divide-slate-800/50">
                    {points.history.slice().reverse().map((h, i) => (
                      <div key={i} className="flex items-center justify-between p-4">
                        <div>
                          <p className="text-white text-sm">{h.reason}</p>
                          <p className="text-slate-600 text-xs">{h.date ? new Date(h.date).toLocaleDateString() : ''}</p>
                        </div>
                        <span className="text-emerald-400 font-bold">+{h.points}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Nominate — Jewel */}
      <JewelModal open={showNominate} onOpenChange={setShowNominate} icon={Star} title="Nominate Staff of the Month" subtitle="Celebrate a teammate's month" accent="amber" size="md" testid="nominate-modal"
        footer={<>
          <JewelButton variant="ghost" onClick={() => setShowNominate(false)}>Cancel</JewelButton>
          <JewelButton accent="amber" onClick={nominate} icon={Star} data-testid="submit-nomination-btn">Submit nomination</JewelButton>
        </>}>
        <div className="space-y-3">
          <JewelField label="Nominee" required>
            <JewelSelect value={nomForm.nominee} onChange={e => setNomForm(f => ({ ...f, nominee: e.target.value }))} data-testid="nominate-select">
              <option value="">Select staff member</option>
              {staffList.map(s => <option key={s.username} value={s.username}>{s.display_name || s.username}</option>)}
            </JewelSelect>
          </JewelField>
          <JewelField label="Category">
            <JewelSelect value={nomForm.category} onChange={e => setNomForm(f => ({ ...f, category: e.target.value }))}>
              {['general', 'teamwork', 'innovation', 'customer-service', 'leadership'].map(c => <option key={c} value={c}>{c}</option>)}
            </JewelSelect>
          </JewelField>
          <JewelField label="Reason">
            <JewelTextarea rows={3} value={nomForm.reason} onChange={e => setNomForm(f => ({ ...f, reason: e.target.value }))} placeholder="Why are you nominating this person?" />
          </JewelField>
        </div>
      </JewelModal>

      {/* Award Badge — Jewel */}
      <JewelModal open={showAwardBadge} onOpenChange={setShowAwardBadge} icon={Award} title="Award Badge" subtitle="Recognise excellence with a badge" accent="violet" size="md" testid="award-badge-modal"
        footer={<>
          <JewelButton variant="ghost" onClick={() => setShowAwardBadge(false)}>Cancel</JewelButton>
          <JewelButton accent="violet" onClick={awardBadge} icon={Award} data-testid="submit-badge-btn">Award badge</JewelButton>
        </>}>
        <div className="space-y-3">
          <JewelField label="Staff member" required>
            <JewelSelect value={badgeForm.target_username} onChange={e => setBadgeForm(f => ({ ...f, target_username: e.target.value }))} data-testid="badge-staff-select">
              <option value="">Select staff</option>
              {staffList.map(s => <option key={s.username} value={s.username}>{s.display_name || s.username}</option>)}
            </JewelSelect>
          </JewelField>
          <JewelField label="Badge" required>
            <JewelSelect value={badgeForm.badge_id} onChange={e => setBadgeForm(f => ({ ...f, badge_id: e.target.value }))} data-testid="badge-select">
              <option value="">Select badge</option>
              {badges.all_badges.map(b => <option key={b.id} value={b.id}>{b.name} ({b.points} pts)</option>)}
            </JewelSelect>
          </JewelField>
          {badgeForm.badge_id && (() => {
            const b = badges.all_badges.find(x => x.id === badgeForm.badge_id);
            if (!b) return null;
            return (
              <div className="p-4 rounded-2xl bg-gradient-to-br from-violet-500/[0.10] to-pink-500/[0.06] border border-violet-400/25 flex items-center gap-3">
                <span className="text-3xl">{b.icon || '🏅'}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium">{b.name}</p>
                  <p className="text-white/50 text-xs">{b.description || 'Milestone achievement'}</p>
                </div>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-violet-500/[0.15] border border-violet-500/30 text-violet-200 text-[11px] font-semibold">+{b.points} pts</span>
              </div>
            );
          })()}
        </div>
      </JewelModal>

      {/* Award Points — Jewel */}
      <JewelModal open={showAwardPoints} onOpenChange={setShowAwardPoints} icon={Award} title="Award Points" subtitle="Boost a teammate's XP" accent="emerald" size="md" testid="award-points-modal"
        footer={<>
          <JewelButton variant="ghost" onClick={() => setShowAwardPoints(false)}>Cancel</JewelButton>
          <JewelButton accent="emerald" onClick={awardPoints} icon={Award} data-testid="submit-points-btn">Award points</JewelButton>
        </>}>
        <div className="space-y-3">
          <JewelField label="Staff member" required>
            <JewelSelect value={pointsForm.target_username} onChange={e => setPointsForm(f => ({ ...f, target_username: e.target.value }))} data-testid="points-staff-select">
              <option value="">Select staff</option>
              {staffList.map(s => <option key={s.username} value={s.username}>{s.display_name || s.username}</option>)}
            </JewelSelect>
          </JewelField>
          <div className="grid grid-cols-3 gap-2">
            {[5, 10, 25, 50, 100, 250].map(q => (
              <button key={q} type="button" onClick={() => setPointsForm(f => ({ ...f, points: q }))}
                className={`px-3 py-2 rounded-xl text-xs font-medium transition-all ${pointsForm.points === q ? 'bg-emerald-500/[0.15] border border-emerald-400/40 text-emerald-200 shadow-[0_0_14px_-4px_rgba(16,185,129,0.55)]' : 'bg-white/[0.02] border border-white/[0.06] text-white/60 hover:text-white'}`}>
                +{q} pts
              </button>
            ))}
          </div>
          <JewelField label="Custom points"><JewelInput type="number" min="1" value={pointsForm.points} onChange={e => setPointsForm(f => ({ ...f, points: parseInt(e.target.value) || 0 }))} /></JewelField>
          <JewelField label="Reason"><JewelInput value={pointsForm.reason} onChange={e => setPointsForm(f => ({ ...f, reason: e.target.value }))} placeholder="Great work on…" /></JewelField>
        </div>
      </JewelModal>
    </div>
  );
};

export default GamificationPage;
